import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Star,
  GraduationCap,
  Heart,
  Briefcase,
  Zap,
  Building2,
  Shield,
  Users,
  User,
  UserCircle2,
  ZoomIn,
  ZoomOut,
  Upload,
  Download,
  MapPin,
  Calendar,
  Award,
  ChevronDown,
} from "lucide-react";

// Bengali numerals converter
const toBengaliNumeral = (num) => {
  const bengaliNumerals = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().split('').map(digit => {
    if (digit >= '0' && digit <= '9') {
      return bengaliNumerals[parseInt(digit)];
    }
    return digit;
  }).join('');
};

// Animated Counter Hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      // Start immediately
      animateCount();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
          animateCount();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [end, hasStarted, startOnView]);

  const animateCount = () => {
    const startTime = Date.now();
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  };

  return { count, ref };
};

// Format number with Bengali comma separators
const formatBengaliNumber = (num) => {
  const str = num.toString();
  let result = '';
  let count = 0;
  for (let i = str.length - 1; i >= 0; i--) {
    if (count === 3 || (count > 3 && (count - 3) % 2 === 0)) {
      result = ',' + result;
    }
    result = str[i] + result;
    count++;
  }
  return toBengaliNumeral(result);
};

// Asset URLs
const ASSETS = {
  candidatePhoto: "https://customer-assets.emergentagent.com/job_mobasher-comilla/artifacts/s0iyayv2_candidate%20pic%202.png",
  bnpSymbol: "https://customer-assets.emergentagent.com/job_vote-resistance/artifacts/x3ve6dzs_Bangladesh_Nationalist_Party_Election_Symbol.svg%20%281%29.png",
  supportFrame: "/frame.png",
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-card/95 backdrop-blur-md shadow-card' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={ASSETS.bnpSymbol} alt="ধানের শীষ" className="w-8 h-8 object-contain" />
            <span className="font-semibold text-foreground">মোবাশ্বের আলম ভুঁইয়া</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollToSection('hero')} className="text-muted-foreground hover:text-primary transition-colors">হোম</button>
            <button onClick={() => scrollToSection('about')} className="text-muted-foreground hover:text-primary transition-colors">পরিচিতি</button>
            <button onClick={() => scrollToSection('vision')} className="text-muted-foreground hover:text-primary transition-colors">আমাদের প্রতিশ্রুতি</button>
            <button onClick={() => scrollToSection('frame')} className="text-muted-foreground hover:text-primary transition-colors">সমর্থন ফ্রেম</button>
          </div>
          <Button 
            onClick={() => scrollToSection('frame')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-4xl px-6"
          >
            সমর্থন ফ্রেম
          </Button>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="min-h-[90vh] bg-white pt-20 relative overflow-hidden">
      {/* Green Doodle Decorations */}
      <svg className="absolute top-24 left-10 w-32 h-32 text-primary/20" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="2" strokeDasharray="8 4"/>
        <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 3"/>
      </svg>
      
      <svg className="absolute top-40 right-20 w-24 h-24 text-primary/15" viewBox="0 0 100 100" fill="none">
        <path d="M20,50 Q50,10 80,50 Q50,90 20,50" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M30,50 Q50,25 70,50 Q50,75 30,50" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
      
      <svg className="absolute bottom-40 left-20 w-20 h-20 text-primary/20" viewBox="0 0 100 100" fill="none">
        <path d="M50,10 L90,90 L10,90 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
      
      <svg className="absolute top-60 left-1/4 w-16 h-16 text-primary/10" viewBox="0 0 100 100" fill="none">
        <rect x="20" y="20" width="60" height="60" rx="10" stroke="currentColor" strokeWidth="2" strokeDasharray="10 5"/>
      </svg>
      
      {/* Dotted pattern doodle */}
      <div className="absolute bottom-32 right-1/4 w-24 h-24">
        <svg viewBox="0 0 100 100" className="text-primary/15">
          {[0, 1, 2, 3, 4].map(row => 
            [0, 1, 2, 3, 4].map(col => (
              <circle key={`${row}-${col}`} cx={10 + col * 20} cy={10 + row * 20} r="3" fill="currentColor"/>
            ))
          )}
        </svg>
      </div>
      
      {/* Curved line doodle */}
      <svg className="absolute top-32 right-1/3 w-40 h-20 text-primary/15" viewBox="0 0 160 80" fill="none">
        <path d="M0,40 Q40,0 80,40 Q120,80 160,40" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
      
      {/* Small star doodles */}
      <svg className="absolute bottom-60 left-1/3 w-12 h-12 text-primary/20" viewBox="0 0 50 50" fill="none">
        <path d="M25,5 L30,20 L45,20 L33,30 L38,45 L25,35 L12,45 L17,30 L5,20 L20,20 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 animate-fade-in relative z-10">
            <Badge className="bg-primary/10 text-primary border-primary/20 rounded-4xl px-4 py-2 text-sm">
              <Star className="w-4 h-4 mr-2 fill-primary" />
              ধানের শীষ প্রতীকে ভোট দিন
            </Badge>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              দূর্নীতি বন্ধ, সংস্কার, এবং উন্নয়ন
            </h1>
            
            <p className="text-xl sm:text-2xl text-primary font-semibold">
              কুমিল্লা <span className="font-bengali-num">১০</span> এর কান্ডারি
            </p>
            
            <div className="space-y-3">
              <p className="text-lg text-foreground font-medium">
                মোবাশ্বের আলম ভুঁইয়া - কুমিল্লা-<span className="font-bengali-num">১০</span> এর প্রার্থী
              </p>
              <p className="text-muted-foreground leading-relaxed">
                গত <span className="font-bengali-num">১৭</span> বছর ফ্যাসিবাদী শাসনামলে অসংখ্য নির্যাতন, মিথ্যা মামলা এবং কারাবরণের শিকার হয়েছেন। 
                জনগণের অধিকার ও ন্যায় প্রতিষ্ঠার সংগ্রামে নিবেদিত প্রাণ।
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-4">
              <Button 
                onClick={() => scrollToSection('frame')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-4xl py-6 text-base font-medium shadow-emerald"
              >
                সমর্থন ফ্রেম
              </Button>
              <Button 
                onClick={() => scrollToSection('about')}
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-4xl py-6 text-base font-medium"
              >
                প্রার্থির সম্পর্কে
              </Button>
            </div>
          </div>
          
          {/* Right Content - Candidate Photo */}
          <div className="relative flex justify-center lg:justify-end animate-slide-in-right">
            <div className="relative">
              <img 
                src={ASSETS.candidatePhoto} 
                alt="মোবাশ্বের আলম ভুঁইয়া" 
                className="w-[320px] sm:w-[380px] lg:w-[450px] h-auto object-contain"
              />
              {/* Floating badge */}
              <div className="absolute -right-2 top-8 animate-float">
                <Badge className="bg-highlight text-highlight-foreground rounded-4xl px-4 py-2 shadow-lg">
                  <Award className="w-4 h-4 mr-2" />
                  প্রতিরোধের কণ্ঠ
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="flex justify-center pt-8">
          <ChevronDown className="w-6 h-6 text-primary animate-bounce-down" />
        </div>
      </div>
    </section>
  );
};

// Info Cards Section
const InfoCardsSection = () => {
  return (
    <section className="py-8 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Symbol Card */}
          <Card className="rounded-4xl border-primary/10 shadow-card card-hover overflow-hidden">
            <CardContent className="p-6 flex items-center gap-6">
              <div className="w-24 h-24 bg-primary-light rounded-3xl flex items-center justify-center p-4 flex-shrink-0">
                <img src={ASSETS.bnpSymbol} alt="ধানের শীষ" className="w-full h-full object-contain" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm mb-1">নির্বাচনী প্রতীক</p>
                <h3 className="text-xl font-bold text-foreground">ধানের শিষ</h3>
                <p className="text-primary font-medium mt-1">বাংলাদেশ জাতীয়তাবাদী দল (বিএনপি)</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Constituency Card */}
          <Card className="rounded-4xl border-primary/10 shadow-card card-hover overflow-hidden">
            <CardContent className="p-6 flex items-center gap-6">
              <div className="w-24 h-24 bg-secondary/10 rounded-3xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-12 h-12 text-secondary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm mb-1">নির্বাচনী এলাকা</p>
                <h3 className="text-xl font-bold text-foreground">কুমিল্লা <span className="font-bengali-num">১০</span> নং আসন</h3>
                <p className="text-secondary font-medium mt-1">নাঙ্গলকোট উপজেলা</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Biography Section
const BiographySection = () => {
  return (
    <section id="about" className="py-16 bg-white relative overflow-hidden">
      {/* Light Green Doodles */}
      <svg className="absolute top-10 right-10 w-24 h-24 text-primary/10" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 4"/>
      </svg>
      
      <svg className="absolute bottom-20 left-10 w-20 h-20 text-primary/10" viewBox="0 0 100 100" fill="none">
        <path d="M20,50 Q50,20 80,50 Q50,80 20,50" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
      
      <svg className="absolute top-1/3 left-5 w-16 h-16 text-primary/8" viewBox="0 0 100 100" fill="none">
        <rect x="25" y="25" width="50" height="50" rx="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="8 4"/>
      </svg>
      
      <svg className="absolute bottom-10 right-20 w-12 h-12 text-primary/10" viewBox="0 0 50 50" fill="none">
        <path d="M25,5 L30,18 L45,18 L33,28 L38,42 L25,33 L12,42 L17,28 L5,18 L20,18 Z" stroke="currentColor" strokeWidth="1" fill="none"/>
      </svg>
      
      {/* Dotted pattern */}
      <div className="absolute top-20 left-1/4 w-16 h-16">
        <svg viewBox="0 0 80 80" className="text-primary/8">
          {[0, 1, 2, 3].map(row => 
            [0, 1, 2, 3].map(col => (
              <circle key={`bio-${row}-${col}`} cx={10 + col * 20} cy={10 + row * 20} r="2" fill="currentColor"/>
            ))
          )}
        </svg>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            পরিচিতি
          </h2>
        </div>
        
        <Card className="rounded-4xl border-primary/10 shadow-card overflow-hidden max-w-5xl mx-auto">
          <CardContent className="p-6 lg:p-10">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              {/* Candidate Image */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mobasher-comilla/artifacts/t3ldlu39_mobahser.png"
                    alt="মোবাশ্বের আলম ভুঁইয়া"
                    className="w-56 h-auto lg:w-64 lg:h-auto rounded-3xl object-contain shadow-lg"
                  />
                  {/* Small decorative element */}
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-primary/10 rounded-full -z-10"></div>
                </div>
              </div>
              
              {/* Biography Text */}
              <div className="flex-1">
                <p className="text-base lg:text-lg text-foreground leading-relaxed text-justify">
                  মোবাশ্বের আলম ভূঁইয়া নাঙ্গলকোট তথা জোড্ডা ইউনিয়নের সাধারণ পরিবারের সন্তান, যিনি তাঁর জীবনকে উৎসর্গ করেছেন জনগণের অধিকার ও ন্যায় প্রতিষ্ঠার সংগ্রামে। তিনি এরশাদ বিরোধী গণতান্ত্রিক আন্দোলনে সক্রিয় অংশগ্রহণের মাধ্যমে ছাত্র রাজনীতিতে প্রবেশ করেন। পরবর্তীতে তিনি শিক্ষার জন্য চট্টগ্রাম চলে যান, সেখানে তিনি ছাত্র রাজনীতির সাথে সক্রিয় ছিলেন।
                </p>
                <p className="text-base lg:text-lg text-foreground leading-relaxed text-justify mt-4">
                  তাকে জাতীয়তাবাদী ছাত্র দলের বিভিন্ন পদে সম্মাননা করা হয়। পরবর্তীতে <span className="font-bengali-num">১৯৯১</span> সালে তার নিজস্ব জন্মভূমিতে (নাঙ্গলকোটে) ফিরে আসেন এবং বাংলাদেশ জাতীয়তাবাদী দলে যোগ দেন। তিনি  রাজনৈতিক মাঠে এবং আপোষহীন নেত্রী বেগম খালেদা জিয়ার হাত শক্তিশালী করতে সক্রিয় ভূমিকা পালন করেন। এই ধারাবাহিকতার অংশ হিসেবে<span className="font-bengali-num">২০০৮</span> সালে দলের মনোনয়নে জাতীয় নির্বাচনে ধানের শীষ প্রতীকে প্রতিদ্বন্দ্বিতা করেন।।
                </p>
                <p className="text-base lg:text-lg text-foreground leading-relaxed text-justify mt-4">
                  গত <span className="font-bengali-num">১৭</span> বছর ফ্যাসিবাদী শাসনামলে অসংখ্য নির্যাতন, মিথ্যা মামলা এবং কারাবরণের শিকার হয়েছেন। তাকে এবং তার দলের কর্মীদের অনেক নির্যাতন এবং জুলুমের মধ্যে দিয়ে যেতে হয়। তিনি এবং তার কর্মীরা <span className="font-bengali-num">২৪</span> এর গন-বিপ্লবে সক্রিয় ভাবে অংশ গ্রাহন করেন এবং ফ্যাসিষ্ট রিজিমের ইতি টানতে সক্রিয় ভূমিকা রাখেন। মোবাশ্বের আলম ভূঁইয়ার জীবন কেটেছে বিপ্লব ও প্রতিরোধের মধ্য দিয়ে।
                </p>
                <p className="text-base lg:text-lg text-primary font-semibold leading-relaxed text-justify mt-4">
                  ফলশ্রুতিতে জননেতা বি এন পির চেয়ারম্যান জনাব তারেক রহমান এবং দলীয় সিদ্ধান্তে ধানের শীষে কুমিল্লা - <span className="font-bengali-num">১০</span> আসনে আবারো মনোনীত হন।
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 mt-8 pt-6 border-t border-border">
              <Badge className="bg-primary/10 text-primary border-primary/20 rounded-4xl px-5 py-2.5 text-base">
                গণতন্ত্রকর্মী
              </Badge>
              <Badge className="bg-highlight/10 text-highlight border-highlight/20 rounded-4xl px-5 py-2.5 text-base">
                প্রতিরোধ যোদ্ধা
              </Badge>
              <Badge className="bg-secondary/10 text-secondary border-secondary/20 rounded-4xl px-5 py-2.5 text-base">
                জনগণের নেতা
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Voter Statistics Section
const VoterStatsSection = () => {
  const stats = {
    total: 536227,
    male: 276647,
    female: 259579,
    third: 1
  };

  const { count: totalCount, ref: totalRef } = useCountUp(stats.total, 2500);
  const { count: maleCount, ref: maleRef } = useCountUp(stats.male, 2000);
  const { count: femaleCount, ref: femaleRef } = useCountUp(stats.female, 2000);
  const { count: thirdCount, ref: thirdRef } = useCountUp(stats.third, 1000);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            ভোটার পরিসংখ্যান
          </h2>
          <p className="text-muted-foreground mt-2">কুমিল্লা <span className="font-bengali-num">১০</span> নং আসন</p>
        </div>
        
        <Card className="rounded-4xl border-primary/10 shadow-card overflow-hidden max-w-3xl mx-auto">
          <CardContent className="p-8">
            {/* Total Voters */}
            <div ref={totalRef} className="text-center mb-8 pb-8 border-b border-border">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground mb-2">মোট ভোটার</p>
              <p className="text-4xl sm:text-5xl font-bold text-primary font-bengali-num">
                {formatBengaliNumber(totalCount)}
              </p>
            </div>
            
            {/* Gender breakdown */}
            <div className="grid grid-cols-3 gap-6">
              <div ref={maleRef} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary/10 rounded-full mb-3">
                  <User className="w-6 h-6 text-secondary" />
                </div>
                <p className="text-muted-foreground text-sm mb-1">পুরুষ</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-bengali-num">
                  {formatBengaliNumber(maleCount)}
                </p>
              </div>
              
              <div ref={femaleRef} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-highlight/10 rounded-full mb-3">
                  <User className="w-6 h-6 text-highlight" />
                </div>
                <p className="text-muted-foreground text-sm mb-1">নারী</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-bengali-num">
                  {formatBengaliNumber(femaleCount)}
                </p>
              </div>
              
              <div ref={thirdRef} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
                  <UserCircle2 className="w-6 h-6 text-primary" />
                </div>
                <p className="text-muted-foreground text-sm mb-1">৩য় লিঙ্গ</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-bengali-num">
                  {toBengaliNumeral(thirdCount)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Vision 2026 Section
const VisionSection = () => {
  const visionItems = [
    {
      icon: GraduationCap,
      title: "মানসম্মত শিক্ষা",
      points: [
        "প্রতিটি ওয়ার্ডে আধুনিক স্কুল",
        "বিনামূল্যে শিক্ষা উপকরণ বিতরণ",
        "মেধাবী শিক্ষার্থীদের বৃত্তি প্রদান"
      ]
    },
    {
      icon: Heart,
      title: "স্বাস্থ্যসেবা",
      points: [
        "ইউনিয়ন পর্যায়ে স্বাস্থ্যকেন্দ্র স্থাপন",
        "বিনামূল্যে চিকিৎসা সেবা",
        "জরুরি অ্যাম্বুলেন্স সার্ভিস"
      ]
    },
    {
      icon: Briefcase,
      title: "ডিজিটাল কর্মসংস্থান",
      points: [
        "যুবকদের আইটি প্রশিক্ষণ",
        "ফ্রিল্যান্সিং হাব স্থাপন",
        "স্থানীয় উদ্যোক্তা উন্নয়ন"
      ]
    },
    {
      icon: Zap,
      title: "স্মার্ট উপজেলা",
      points: [
        "ডিজিটাল সেবা কেন্দ্র",
        "ফ্রি ওয়াইফাই জোন",
        "ই-গভার্নেন্স বাস্তবায়ন"
      ]
    },
    {
      icon: Building2,
      title: "অবকাঠামো উন্নয়ন",
      points: [
        "সকল রাস্তা পাকাকরণ",
        "ব্রিজ ও কালভার্ট নির্মাণ",
        "পয়ঃনিষ্কাশন ব্যবস্থা উন্নয়ন"
      ]
    },
    {
      icon: Shield,
      title: "দুর্নীতিমুক্ত শাসন",
      points: [
        "স্বচ্ছ প্রশাসন নিশ্চিতকরণ",
        "জনগণের কাছে জবাবদিহিতা",
        "অনলাইনে অভিযোগ নিষ্পত্তি"
      ]
    }
  ];

  return (
    <section id="vision" className="py-16 bg-gradient-to-b from-accent/30 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-primary/10 text-primary border-primary/20 rounded-4xl px-4 py-2 mb-4">
          
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            আমাদের প্রতিশ্রুতি
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            কুমিল্লা ১০ এলাকার সামগ্রিক উন্নয়নে আমাদের  মূল পরিকল্পনা
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visionItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <Card key={index} className="vision-card rounded-4xl border-primary/10 shadow-card card-hover overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">{item.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {item.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-muted-foreground">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Countdown Timer Section
const CountdownSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetDate = new Date('2026-02-12T00:00:00');
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const TimeBox = ({ value, label }) => (
    <div className="countdown-box rounded-3xl p-4 sm:p-6 text-center text-primary-foreground">
      <p className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-1 font-bengali-num">
        {toBengaliNumeral(value.toString().padStart(2, '0'))}
      </p>
      <p className="text-sm opacity-90">{label}</p>
    </div>
  );

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">নির্বাচনের তারিখ</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            <span className="font-bengali-num">১২</span> ফেব্রুয়ারি <span className="font-bengali-num">২০২৬</span>
          </h2>
          <p className="text-muted-foreground mt-2">নির্বাচন পর্যন্ত বাকি সময়</p>
        </div>
        
        <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
          <TimeBox value={timeLeft.days} label="দিন" />
          <TimeBox value={timeLeft.hours} label="ঘণ্টা" />
          <TimeBox value={timeLeft.minutes} label="মিনিট" />
          <TimeBox value={timeLeft.seconds} label="সেকেন্ড" />
        </div>
        
        <div className="flex justify-center mt-8">
          <img src={ASSETS.bnpSymbol} alt="ধানের শীষ" className="w-16 h-16 opacity-60" />
        </div>
      </div>
    </section>
  );
};

// Support Frame Generator Section
const FrameGeneratorSection = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [scale, setScale] = useState([1]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDownloading, setIsDownloading] = useState(false);
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  // Preload frame image as base64
  useEffect(() => {
    const loadFrame = async () => {
      try {
        console.log("Loading frame from:", ASSETS.supportFrame);
        const response = await fetch(ASSETS.supportFrame);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log("Frame loaded successfully as base64");
          setFrameImage(reader.result);
        };
        reader.onerror = () => {
          console.error("FileReader error");
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Could not preload frame:", error);
      }
    };
    loadFrame();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setScale([1]);
        setPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    if (!uploadedImage) return;
    setIsDragging(true);
    const rect = previewRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left - dragStart.x,
      y: e.clientY - rect.top - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (!uploadedImage) return;
    setIsDragging(true);
    const rect = previewRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    setDragStart({
      x: touch.clientX - rect.left - position.x,
      y: touch.clientY - rect.top - position.y
    });
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - rect.left - dragStart.x,
      y: touch.clientY - rect.top - dragStart.y
    });
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  const downloadImage = async () => {
    if (!uploadedImage) return;
    
    setIsDownloading(true);
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const size = 500;
      canvas.width = size;
      canvas.height = size;
      
      // Clear canvas
      ctx.clearRect(0, 0, size, size);
      
      // Draw user image first (already loaded as base64 from file input - no CORS issue)
      const userImg = new Image();
      
      await new Promise((resolve, reject) => {
        userImg.onload = resolve;
        userImg.onerror = reject;
        userImg.src = uploadedImage;
      });
      
      const scaleFactor = scale[0];
      const imgWidth = size * scaleFactor;
      const imgHeight = (userImg.height / userImg.width) * imgWidth;
      const offsetX = (size - imgWidth) / 2 + (position.x / 150 * size / 2);
      const offsetY = (size - imgHeight) / 2 + (position.y / 150 * size / 2);
      
      // Create circular clipping path
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();
      
      // Draw user image
      ctx.drawImage(userImg, offsetX, offsetY, imgWidth, imgHeight);
      ctx.restore();
      
      // Draw frame overlay - use preloaded base64 if available
      if (frameImage) {
        const frameImg = new Image();
        await new Promise((resolve) => {
          frameImg.onload = resolve;
          frameImg.onerror = resolve;
          frameImg.src = frameImage;
        });
        
        if (frameImg.complete && frameImg.naturalWidth > 0) {
          ctx.drawImage(frameImg, 0, 0, size, size);
        }
      }
      
      // Convert to data URL and download
      const dataUrl = canvas.toDataURL('image/png');
      
      // Create download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'support-mobasher-alam.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("Download error:", error);
      
      // Fallback: Download without frame
      try {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const size = 500;
        canvas.width = size;
        canvas.height = size;
        ctx.clearRect(0, 0, size, size);
        
        const userImg = new Image();
        await new Promise((resolve) => {
          userImg.onload = resolve;
          userImg.src = uploadedImage;
        });
        
        const scaleFactor = scale[0];
        const imgWidth = size * scaleFactor;
        const imgHeight = (userImg.height / userImg.width) * imgWidth;
        const offsetX = (size - imgWidth) / 2 + (position.x / 150 * size / 2);
        const offsetY = (size - imgHeight) / 2 + (position.y / 150 * size / 2);
        
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(userImg, offsetX, offsetY, imgWidth, imgHeight);
        ctx.restore();
        
        // Draw simple frame border
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 5, 0, Math.PI * 2);
        ctx.strokeStyle = '#059669';
        ctx.lineWidth = 10;
        ctx.stroke();
        
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'support-mobasher-alam.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
      } catch (e) {
        alert("ডাউনলোড করতে সমস্যা হয়েছে। আবার চেষ্টা করুন।");
      }
    }
    
    setIsDownloading(false);
  };

  return (
    <section id="frame" className="py-16 bg-gradient-to-b from-background to-accent/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-primary/10 text-primary border-primary/20 rounded-4xl px-4 py-2 mb-4">
            সমর্থন প্রকাশ করুন
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            সমর্থন ফ্রেম জেনারেটর
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            আপনার ছবিতে সমর্থন ফ্রেম যুক্ত করুন এবং সামাজিক মাধ্যমে শেয়ার করুন
          </p>
        </div>
        
        <Card className="rounded-4xl border-primary/10 shadow-card overflow-hidden max-w-2xl mx-auto">
          <CardContent className="p-6 sm:p-8">
            {/* Step 1: Upload */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  ১
                </div>
                <h3 className="font-semibold text-foreground">ছবি আপলোড করুন</h3>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 rounded-3xl py-8"
              >
                <Upload className="w-5 h-5 mr-2 text-primary" />
                <span className="text-primary">ছবি নির্বাচন করুন</span>
              </Button>
            </div>
            
            {/* Preview */}
            {uploadedImage && (
              <>
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                      ২
                    </div>
                    <h3 className="font-semibold text-foreground">সামঞ্জস্য করুন</h3>
                  </div>
                  
                  {/* Preview area */}
                  <div 
                    ref={previewRef}
                    className="relative w-[300px] h-[300px] mx-auto rounded-full overflow-hidden cursor-move bg-muted"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  >
                    <img
                      src={uploadedImage}
                      alt="User"
                      className="absolute w-full h-full object-cover"
                      style={{
                        transform: `scale(${scale[0]}) translate(${position.x / scale[0]}px, ${position.y / scale[0]}px)`,
                        transformOrigin: 'center'
                      }}
                      draggable={false}
                    />
                    <img
                      src={frameImage || ASSETS.supportFrame}
                      alt="Frame"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      draggable={false}
                    />
                  </div>
                  
                  {/* Zoom slider */}
                  <div className="flex items-center gap-4 mt-6 max-w-xs mx-auto">
                    <ZoomOut className="w-5 h-5 text-muted-foreground" />
                    <Slider
                      value={scale}
                      onValueChange={setScale}
                      min={0.5}
                      max={2}
                      step={0.1}
                      className="flex-1"
                    />
                    <ZoomIn className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    ড্র্যাগ করে ছবি সরান, স্লাইডার দিয়ে জুম করুন
                  </p>
                </div>
                
                {/* Step 3: Download */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                      ৩
                    </div>
                    <h3 className="font-semibold text-foreground">ডাউনলোড করুন</h3>
                  </div>
                  <Button
                    onClick={downloadImage}
                    disabled={isDownloading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-3xl py-6 text-base font-medium shadow-emerald disabled:opacity-50"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {isDownloading ? 'ডাউনলোড হচ্ছে...' : 'ছবি ডাউনলোড করুন'}
                  </Button>
                </div>
              </>
            )}
            
            {/* Hidden canvas for image processing */}
            <canvas ref={canvasRef} className="hidden" />
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 bg-background border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-muted-foreground">
          সৌজন্যে: মাহাজ
        </p>
        <p className="text-center text-muted-foreground/60 text-xs mt-3">
          Note: This website has been created using publicly available information, and was built by Mahaz in support of his brother, Mobasher Alam Bhuiyan.
        </p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-background font-bengali">
      <Navigation />
      <HeroSection />
      <InfoCardsSection />
      <BiographySection />
      <VoterStatsSection />
      <VisionSection />
      <CountdownSection />
      <FrameGeneratorSection />
      <Footer />
    </div>
  );
}

export default App;
